import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {catchError, of} from 'rxjs';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatError, MatFormField, MatHint, MatInput, MatLabel} from '@angular/material/input';
import {MatIcon} from '@angular/material/icon';
import {CiSelectComponent} from '../../../../profiler-theme';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ModalComponent} from '../modal/modal.component';
import {MessageService} from '../message.service';
import {requestProblemOf, RequestsService} from '../../../services/requests.service';
import {
  hasForbiddenCharacters,
  isFilled,
  requestLanguageFor,
  RequestLanguage,
  requestNameParts,
  Requests,
  RequestsFactory,
  RequestType,
  toLanguageRequest,
} from '../../model/request.model';
import {INPUT_LIMITS} from '../../constants/input-limits';
import {FaehigkeitenService} from '../../../services/faehigkeiten.service';
import {ISkillCategory} from '../../model/skill-management.model';


/**
 * Anfrage einer Fähigkeit, Branche oder eines Schwerpunkts in der Oberflächensprache,
 * mit Dublettenprüfung über beide Sprachen vor dem Abschicken.
 */
@Component({
  selector: 'app-request-dialog',
  imports: [
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatHint,
    MatIcon,
    CiSelectComponent,
    TranslatePipe,
    ModalComponent,
    MatButton,
  ],
  templateUrl: './request-dialog.component.html',
  styleUrl: './request-dialog.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestDialogComponent implements OnChanges {
  @Input({required: true}) type: RequestType;
  @Input({required: true}) modalId: string;
  @Input() title: string;
  @Input() saveText = 'request.modals.submit';
  @Input() showInfo = true;
  /** Ob ein freigegebener Treffer statt der Anfrage übernommen werden kann. */
  @Input() allowExisting = true;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() existingChosen = new EventEmitter<Requests>();
  @Output() requested = new EventEmitter<void>();

  protected get LIMITS() { return INPUT_LIMITS; }
  protected get RequestType() { return RequestType; }
  protected readonly languages: RequestLanguage[] = ['de', 'en'];

  language: RequestLanguage = 'de';
  draft: Requests = RequestsFactory.empty();
  duplicate: Requests | null = null;
  showDuplicateModal = false;
  submitting = false;
  /** Die feste Kategorieliste; eine Fähigkeit wird nur einer vorhandenen Kategorie zugeordnet (NP20-1186). */
  categories: ISkillCategory[] = [];
  private keepDraftOnOpen = false;

  constructor(
    private translate: TranslateService,
    private requestsService: RequestsService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private skillService: FaehigkeitenService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']?.currentValue && !changes['visible'].previousValue) {
      if (!this.keepDraftOnOpen) this.reset();
      this.keepDraftOnOpen = false;
      if (this.type === RequestType.SKILL) this.loadCategories();
    }
  }

  /** Kategorienamen in der Sprache des Antrags; auf Englisch nur Kategorien, die einen englischen Namen haben. */
  get categoryOptions(): string[] {
    return this.categories
      .map(category => this.categoryName(category, this.language))
      .filter((name): name is string => isFilled(name))
      .sort((a, b) => a.localeCompare(b, this.language));
  }

  get draftValues(): (string | null)[] {
    return this.type === RequestType.SKILL ? [this.draft.kategorie, this.draft.faehigkeit] : [this.draft.name];
  }

  get hasControlCharacters(): boolean {
    return this.draftValues.some(hasForbiddenCharacters);
  }

  get draftInvalid(): boolean {
    return this.submitting || !this.draftValues.every(isFilled) || this.hasControlCharacters;
  }

  get canUseDuplicate(): boolean {
    return this.allowExisting && !!this.duplicate?.approved;
  }

  onVisibleChange(visible: boolean) {
    this.visible = visible;
    this.visibleChange.emit(visible);
  }

  submit() {
    if (this.draftInvalid) return;
    this.submitting = true;
    // Die Dublettenprüfung ist eine Komfortfunktion: scheitert sie, wird trotzdem angelegt,
    // und das Backend lehnt eine echte Dublette mit 409 ab.
    this.requestsService.findDuplicates(this.type, this.draft)
      .pipe(catchError(() => of([] as Requests[])))
      .subscribe(hits => {
        const found = hits ?? [];
        const hit = found.find(h => h.approved) ?? found[0];
        if (hit) {
          this.submitting = false;
          this.duplicate = hit;
          this.showDuplicateModal = true;
          this.cdr.markForCheck();
          return;
        }
        this.create();
      });
  }

  useDuplicate() {
    if (this.canUseDuplicate) this.existingChosen.emit(this.duplicate);
    this.closeDuplicate();
  }

  /** X, Hintergrund und „Schließen" beenden die Rückfrage, ohne zur Eingabe zurückzukehren. */
  closeDuplicate() {
    this.duplicate = null;
    this.showDuplicateModal = false;
  }

  changeInput() {
    this.showDuplicateModal = false;
    this.editAgain();
  }

  /** Zurück zur Eingabe, der getippte Text und die Sprache bleiben stehen. */
  editAgain() {
    this.duplicate = null;
    // Hat der Benutzer den Dialog inzwischen neu geöffnet, gehört ihm der frische Stand.
    if (this.visible) return;
    this.keepDraftOnOpen = true;
    this.onVisibleChange(true);
    this.cdr.markForCheck();
  }

  duplicateNames(language: RequestLanguage): string | null {
    const parts = requestNameParts(this.duplicate, this.type, language).filter(isFilled);
    return parts.length ? parts.join(' – ') : null;
  }

  private create() {
    this.requestsService.newRequest(this.type, toLanguageRequest(this.draft, this.language)).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.success('request.modals.successEditing');
        this.requested.emit();
        this.cdr.markForCheck();
      },
      error: err => {
        this.submitting = false;
        const problem = requestProblemOf(err);
        this.messageService.error(
          problem === 'alreadyExists' ? 'request.modals.alreadyExists'
            : problem === 'oneLanguageExpected' ? 'request.modals.oneLanguageExpected'
              : 'request.modals.errorEditing',
        );
        this.editAgain();
      },
    });
  }

  private categoryName(category: ISkillCategory, language: RequestLanguage): string | null {
    return (language === 'en' ? category.kategorieEn : category.kategorie) ?? null;
  }

  private loadCategories() {
    this.skillService
      .categories()
      .pipe(
        catchError(() => {
          this.messageService.error('request.modals.categoriesLoadingError');
          return of([] as ISkillCategory[]);
        }),
      )
      .subscribe((categories) => {
        this.categories = categories;
        this.cdr.markForCheck();
      });
  }

  private reset() {
    this.language = requestLanguageFor(this.translate.getCurrentLang() ?? this.translate.getDefaultLang());
    this.draft = RequestsFactory.empty();
    this.duplicate = null;
    this.submitting = false;
  }
}
